import { FeedbackType, TEXT_LIMITS, values } from '@posilka/shared';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@posilka/client/api/client';
import { MainAction } from '@/components/MainAction';
import { Section } from '@/components/Section';
import { Segmented } from '@/components/Segmented';
import { useErrorMessage } from '@/hooks/useErrorMessage';
import { haptic } from '@/lib/telegram';
import { CharHint } from '@/components/Field';

/** Umumiy murojaat (taklif / shikoyat / xato) — buyurtmaga bog'lanmagan, adminlarga boradi. */
export function FeedbackPage() {
  const { t } = useTranslation();
  const errorMessage = useErrorMessage();
  const [type, setType] = useState<FeedbackType>(FeedbackType.SUGGESTION);
  const [text, setText] = useState('');
  const [sent, setSent] = useState(false);
  const submit = useMutation({
    mutationFn: () =>
      api<{ id: string }>('/feedback', { method: 'POST', body: { type, text: text.trim() } }),
    onSuccess: () => {
      haptic.success();
      setSent(true);
      setText('');
    },
    onError: () => haptic.error(),
  });

  return (
    <>
      <h1 className="mb-2 px-1 font-display text-2xl font-bold tracking-tight">
        {t('feedback.title')}
      </h1>
      <p className="mb-4 px-1 text-sm text-hint">{t('feedback.intro')}</p>

      {sent ? (
        <Section>
          <div className="space-y-3 p-4 text-center">
            <p className="text-3xl">✅</p>
            <p className="font-medium">{t('feedback.sent')}</p>
            <button type="button" className="btn-secondary w-full" onClick={() => setSent(false)}>
              {t('feedback.sendAnother')}
            </button>
          </div>
        </Section>
      ) : (
        <>
          <Section title={t('feedback.type')}>
            <div className="p-3">
              <Segmented<FeedbackType>
                value={type}
                onChange={(v) => {
                  haptic.tap();
                  setType(v);
                }}
                options={values(FeedbackType).map((v) => ({
                  value: v,
                  label: t(`feedback.types.${v}`),
                }))}
              />
            </div>
          </Section>
          <Section title={t('feedback.text')}>
            <div className="p-3">
              <textarea
                className="input min-h-36 resize-none"
                placeholder={t('feedback.placeholder')}
                maxLength={TEXT_LIMITS.feedback.max}
                value={text}
                onChange={(e) => setText(e.target.value)}
              />
              <CharHint count={{ value: text, ...TEXT_LIMITS.feedback }} />
            </div>
          </Section>
          {submit.isError && (
            <p className="px-1 text-sm text-destructive">{errorMessage(submit.error)}</p>
          )}
          <MainAction
            text={t('feedback.submit')}
            enabled={text.trim().length >= TEXT_LIMITS.feedback.min}
            loading={submit.isPending}
            onClick={() => submit.mutate()}
          />
        </>
      )}
    </>
  );
}
